import { RefObject, useEffect, useRef, useState } from 'react';
import type { Wallet } from '@tonconnect/sdk';
import { RegisterOptions } from 'react-hook-form';
import type Reaptcha from 'reaptcha';
import useLoginOrSignupSubmit from '~/client/components/Auth/Authenticate/AuthSteps/hooks/helpers/useLoginOrSignupSubmit';
import useRedirectUrl from '~/client/components/Auth/hooks/useRedirectUrl';
import useAuthCaptchaClearance from '~/client/components/Auth/Shared/utils/useAuthCaptchaClearance';
import useHandleCompleteRegistration from '~/client/features/CompleteRegistration/hooks/useHandleCompleteRegistration';
import useAuthMetamaskUser from '~/client/features/Metamask/hooks/useAuthMetamaskUser';
import useSocialAuthLocale from '~/client/features/SocialAuth/hooks/useSocialAuthLocale';
import useAuthTonConnectUser from '~/client/features/TonConnect/hooks/useAuthTonConnectUser';
import useForm from '~/client/hooks/form/useForm';
import useTranslation from '~/client/hooks/i18n/useTranslation';
import useQueryParam from '~/client/hooks/router/useQueryParam';
import useRouter from '~/client/hooks/router/useRouter';
import useSite from '~/client/hooks/useSite';
import dateObjectFromDateString from '~/client/shared/form/FormDateInput/DateInput/useDateInput/helpers/dateObjectFromDateString';
import formatDateToMonthDayYear from '~/client/shared/form/FormDateInput/DateInput/useDateInput/helpers/formatDateTime';
import getTimeFromDayMonthYear from '~/client/shared/form/FormDateInput/DateInput/useDateInput/helpers/getTimeFromDayMonthYear';
import useCountryCodeSelect from '~/client/shared/form/FormSelect/useCountryCodeSelect';
import sendFormErrorGtm from '~/shared/gtm/sendFormErrorGtm';
import useCookie from '~/shared/hooks/useCookie';
import trackEvent from '~/shared/trackEvent';
import deserializeErrors from '~/shared/utils/errors/deserializeErrors';
import SessionStorage from '~/shared/utils/storage/SessionStorage';
import type FormValues from './FormValues';
import useHandleRegister from './useHandleRegister';
import useHandlePayloadResponse from './useHandleRegister/useHanldePayloadResponse';

type Props = {
  isPopover?: boolean;
  onSuccess?: () => void;
  defaultValues?: {
    offers: boolean;
    offersSms?: boolean;
    offersCall?: boolean;
    acceptsTerms?: boolean;
    email?: string;
    username?: string;
  };
};

const useFormRegister = ({ onSuccess, isPopover, defaultValues }: Props) => {
  const [showEmailSuggestion, setShowEmailSuggestion] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRegistrationStart, setRegistrationStart] = useState(false);
  const { push } = useRouter();
  const redirectUrl = useRedirectUrl();
  const [isMetamask] = useQueryParam('metamask');
  const [isTonConnect] = useQueryParam('tonconnect');
  const { siteConfig } = useSite();
  const { socialAuthType } = useLoginOrSignupSubmit({});
  const {
    handlePayloadResponse,
    hasCaptchaRequiredError,
    loading: payloadLoading,
  } = useHandlePayloadResponse({ isPopover, onSuccess });

  const { handleRegister } = useHandleRegister(hasCaptchaRequiredError);
  const { handleMetamaskAuth } = useAuthMetamaskUser();
  const { handleTonConnectAuth } = useAuthTonConnectUser();
  const tonConnectWallet = SessionStorage.getItem('tonConnectWallet') as Wallet;
  const { loading: loadingSocials, handleCompleteRegistration } =
    useHandleCompleteRegistration();
  const [getLocale] = useSocialAuthLocale();
  const [, setCookie] = useCookie('existingEmail');
  const { isCaptchaClearedRequired } = useAuthCaptchaClearance();

  const {
    formState,
    control,
    errors,
    required,
    optional,
    handleSubmit,
    setValue,
    getValues,
    setErrors,
    setError,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: defaultValues
      ? { ...defaultValues }
      : {
          offers: true,
          offersSms: true,
          offersCall: true,
          acceptsTerms: false,
        },
    validationMode: 'all',
  });

  const offersLabel = useTranslation('profile.offers.site');
  const termsLabel = useTranslation(
    'profile.i_agree_to_terms_and_conditions_and_privacy_policy',
  );
  const { isValid, isDirty, dirtyFields } = formState;
  const dirtyFieldsList = Object.keys(dirtyFields);

  const isSubmitDisabled = !isValid || !isDirty;

  const recaptchaRef = useRef<Reaptcha>(null);
  useEffect(() => {
    if (!isRegistrationStart && dirtyFieldsList.length) {
      setRegistrationStart(true);
      trackEvent({}, 'registrationStart');
    }
  }, [dirtyFieldsList.length, isRegistrationStart]);

  const callingCodes = useCountryCodeSelect<FormValues>({
    autofillFromGeo: false,
    onChange: setValue,
    value: getValues().countryCode || '',
  });

  const otherCountryCodesText = useTranslation('general.other_country_codes');

  const handleSelectedSuggestionEmail = (selectedEmail: string) => {
    const currentValue = getValues().email;
    const newValue = currentValue.concat(selectedEmail);
    setValue('email', newValue);
    setShowEmailSuggestion(false);
  };

  const currentDate = new Date();
  const defaultDate = formatDateToMonthDayYear(currentDate);

  const invalidUsernameErrorMsg = useTranslation('errors.INVALID_USERNAME');
  const invalidEmailErrorMsg = useTranslation('errors.INVALID_EMAIL');
  const longEmailErrorMsg = useTranslation('errors.input.max_length');

  const usernameInputOptions: RegisterOptions = {
    pattern: {
      value: /^[A-Za-z0-9_]{3,20}$/,
      message: invalidUsernameErrorMsg,
    },
  };

  const emailInputOptions: RegisterOptions = {
    maxLength: {
      value: 80,
      message: longEmailErrorMsg,
    },
    pattern: {
      value:
        /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
      message: invalidEmailErrorMsg,
    },
  };

  return {
    control,
    offersLabel,
    formState,
    termsLabel,
    loading: loading || loadingSocials || payloadLoading,
    errors,
    isCaptchaRequired: isCaptchaClearedRequired || hasCaptchaRequiredError,
    recaptchaRef: recaptchaRef as RefObject<Reaptcha>,
    callingCodes,
    isSubmitDisabled,
    required,
    setValue,
    getValues,
    optional,
    setError,
    otherCountryCodesText,
    showEmailSuggestion,
    handleSelectedSuggestionEmail,
    handlePayloadResponse,
    usernameInputOptions,
    emailInputOptions,
    handleSubmit: handleSubmit(async (values) => {
      setLoading(true);
      clearErrors();
      const { DateTime } = await import('luxon');
      const dateOfBirth =
        typeof values.dateOfBirth === 'string'
          ? dateObjectFromDateString(values.dateOfBirth ?? defaultDate)
          : values.dateOfBirth;
      const isDateOfBirthValid = DateTime.fromObject(
        values.dateOfBirth,
      ).isValid;

      const timestamp = getTimeFromDayMonthYear(dateOfBirth);

      if (!isDateOfBirthValid || timestamp === undefined) {
        setLoading(false);
        return setError('dateOfBirth.day', { message: 'errors.INVALID_DATE' });
      }

      if (isMetamask) {
        return handleMetamaskAuth({
          values: { ...values, dateOfBirth: timestamp },
          setErrors,
          setError,
          redirectUrl,
        });
      }

      if (isTonConnect) {
        return handleTonConnectAuth({
          walletInfo: tonConnectWallet,
          values: { ...values, dateOfBirth: timestamp },
          setErrors,
          setError,
          redirectUrl,
        });
      }

      const socialAuthError = sessionStorage.getItem('social_auth_error');
      if (socialAuthType && !socialAuthError) {
        const payload = await handleCompleteRegistration({
          ...values,
          dateOfBirth: timestamp,
        });
        if (payload.errors) {
          recaptchaRef.current?.reset();
          setErrors(payload.errors as GenericErrorType[]);
          const deserializedErrors = deserializeErrors(
            payload.errors,
          ) as DeserializeError;
          setLoading(false);
          if (deserializedErrors.dateOfBirth) {
            setLoading(false);
            const [message] = deserializedErrors.dateOfBirth;
            setError('dateOfBirth.day', { message: `errors.${message}` });
          }
          return sendFormErrorGtm({
            event: 'registrationFail',
            errors: payload?.errors as GenericErrorType[],
          });
        }
        return push(redirectUrl, undefined, {
          shallow: false,
          locale:
            getLocale() === siteConfig.defaultLang ? undefined : getLocale(),
        });
      }
      const payload = await handleRegister({
        ...values,
        dateOfBirth: timestamp,
      });
      setLoading(false);
      if (payload.errors) {
        recaptchaRef.current?.reset();
        setErrors(payload.errors as GenericErrorType[]);
        const deserializedErrors = deserializeErrors(
          payload.errors,
        ) as DeserializeError;
        if (
          deserializedErrors.email &&
          deserializedErrors.email[0] === 'EMAIL_ALREADY_EXISTS'
        ) {
          setCookie(values.email);
          setLoading(false);
          push('/login');
        }
      }
      return handlePayloadResponse(payload, setErrors);
    }),
  };
};

export default useFormRegister;
